<template>
  <ion-card class="dashboard-container">
    <div v-if="loading" class="loading-container">
      <ion-spinner name="crescent"></ion-spinner>
      <p>Loading dashboard...</p>
    </div>
    <div v-else-if="error" class="error-container">
      <ion-icon :icon="alertCircleOutline" size="large"></ion-icon>
      <p>{{ errorMessage }}</p>
    </div>
    <div v-else-if="!dashboardUrl" class="no-dashboard">
      <ion-icon :icon="documentOutline" size="large"></ion-icon>
      <p>No dashboard configured for this dealer</p>
    </div>
    <iframe
      v-else
      :src="dashboardUrl"
      class="dashboard-frame"
      allow="fullscreen"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      referrerpolicy="no-referrer"
    ></iframe>
  </ion-card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { IonCard, IonSpinner, IonIcon } from '@ionic/vue';
import { alertCircleOutline, documentOutline } from 'ionicons/icons';
import { supabase } from '@/supabase';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();
const dashboardUrl = ref<string | null>(null);
const loading = ref(true);
const error = ref(false);
const errorMessage = ref('Unable to load dashboard');

const loadDashboardUrl = async () => {
  loading.value = true;
  error.value = false;
  
  const currentUser = userStore.user.value;
  if (!currentUser || !currentUser.rooftop_id) {
    error.value = true;
    errorMessage.value = 'User information not available';
    loading.value = false;
    return;
  }
  
  try {
    // Get the embed URL from the dealers table
    const { data: dealer, error: dealerError } = await supabase
      .from('dealers')
      .select('embed_url')
      .eq('rooftop_id', currentUser.rooftop_id)
      .single();
      
    if (dealerError) throw dealerError;
    
    dashboardUrl.value = dealer.embed_url;
    loading.value = false;
  } catch (err) {
    console.error('Error loading dashboard URL:', err);
    error.value = true;
    errorMessage.value = 'Error loading dashboard configuration';
    loading.value = false;
  }
};

// Load dashboard URL on component mount
onMounted(async () => {
  await loadDashboardUrl();
});

// Watch for user changes to refresh dashboard URL
watch(() => userStore.user.value?.rooftop_id, async (newRooftopId) => {
  if (newRooftopId) {
    await loadDashboardUrl();
  }
});
</script>

<style scoped>
.dashboard-container {
  height: calc(100vh - 80px);
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  overflow: hidden;
  box-shadow: none;
  border-radius: 12px;
  flex: 1;
}

.dashboard-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.loading-container,
.error-container,
.no-dashboard {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: var(--ion-color-medium);
}

.loading-container ion-spinner,
.error-container ion-icon,
.no-dashboard ion-icon {
  margin-bottom: 16px;
  color: var(--ion-color-medium);
}
</style> 